import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './Attendance.css';

import { formatDateOnly, todayInZone, todayInZone30Ahead } from '../utils/dateOnly';
const Attendance = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('today');
  const [outToday, setOutToday] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [workersCompCases, setWorkersCompCases] = useState([]);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Filters
  const [dateRange, setDateRange] = useState({
    // TO1b.2: toISOString() is UTC, so after 20:00 ET these defaulted to
    // TOMORROW and the history view silently dropped today's rows.
    start: todayInZone(),
    end: todayInZone30Ahead()
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Modal states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [showWorkersCompModal, setShowWorkersCompModal] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchAllRequests();
    }
  }, [activeTab, dateRange, statusFilter, typeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [outTodayRes, pendingRes, typesRes, wcRes] = await Promise.all([
        api.get('/time-off/out-today'),
        api.get('/time-off/requests/pending'),
        api.get('/time-off/types'),
        api.get('/time-off/workers-comp')
      ]);
      
      setOutToday(outTodayRes.data.employees || []);
      setPendingRequests(pendingRes.data.requests || []);
      setTimeOffTypes(typesRes.data.types || []);
      setWorkersCompCases(wcRes.data.cases || []);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllRequests = async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type_id', typeFilter);
      
      const res = await api.get(`/time-off/requests/all?${params.toString()}`);
      setAllRequests(res.data.requests || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  // TO1b stage vocabulary
  const STAGE_LABEL = {
    pending_manager: 'Awaiting manager',
    pending_gm: 'Awaiting GM',
    approved: 'Approved',
    denied: 'Denied',
    cancelled: 'Cancelled'
  };

  const handleReview = (request) => {
    setSelectedRequest(request);
    setReviewNotes('');
    setReviewError('');
    setShowReviewModal(true);
  };

  // TO1b: the contract is {decision, notes}. Ruling 6 makes notes REQUIRED on
  // a denial — enforced server-side; this only saves the round trip.
  const submitReview = async (decision) => {
    if (!selectedRequest) return;
    if (decision === 'deny' && !reviewNotes.trim()) {
      setReviewError('Notes are required when denying a request');
      return;
    }
    try {
      setReviewError('');
      setActionLoading(selectedRequest.id);
      await api.put(`/time-off/requests/${selectedRequest.id}/review`, {
        decision,
        notes: reviewNotes
      });
      await fetchData();
      setShowReviewModal(false);
      setSelectedRequest(null);
    } catch (error) {
      setReviewError(error.response?.data?.message || 'Failed to review request');
    } finally {
      setActionLoading(null);
    }
  };

  // Ruling 7: an APPROVED request may be cancelled by the department manager
  // or the GM. Uses its own route, not the review route.
  const cancelApproved = async (request) => {
    const notes = window.prompt('Cancelling this approved time off. Note (optional):');
    if (notes === null) return;
    try {
      setActionLoading(request.id);
      await api.put(`/time-off/requests/${request.id}/cancel`, { notes });
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to cancel');
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewCase = async (caseId) => {
    try {
      const res = await api.get(`/time-off/workers-comp/${caseId}`);
      setSelectedCase(res.data.case);
      setShowWorkersCompModal(true);
    } catch (error) {
      console.error('Error fetching case:', error);
    }
  };

  const handleCloseCase = async (caseId) => {
    if (!window.confirm('Are you sure you want to close this Workers Comp case?')) return;
    
    try {
      await api.put(`/time-off/workers-comp/${caseId}/close`, {
        // TO1b.2: the return date is a CIVIL date. toISOString() is UTC, so
        // closing a case after 20:00 ET stamped tomorrow.
        actual_return_date: todayInZone()
      });
      await fetchData();
      setShowWorkersCompModal(false);
    } catch (error) {
      console.error('Error closing case:', error);
    }
  };

  // TO1b.2: every value this formats is a DATE-only column. Going through
  // new Date() parsed them as UTC midnight and rendered them a day early in
  // ET — an approved Mon–Fri block showed as Sun–Thu. formatDateOnly works
  // on the parts, so it reads the same in every timezone.
  // The 'N/A' fallback is preserved deliberately: several call sites read
  // `formatDate(x) || 'TBD'`, and changing the empty return to '' would make
  // those branches fire for the first time. Out of scope for a date fix.
  const formatDate = (dateStr) => (dateStr ? formatDateOnly(dateStr) : 'N/A');

  // TO1c / ruling 11: the approver sees the same warning the requester saw.
  // blackout_warnings is computed at READ time by the server (never stored),
  // so a blackout added, edited or lifted after submit is reflected the next
  // time this page loads. It is a badge, not a gate — Review works unchanged.
  const BlackoutBadge = ({ warnings }) => {
    if (!warnings || warnings.length === 0) return null;
    const reasons = warnings.map(w =>
      `${formatDate(w.start_date)} – ${formatDate(w.end_date)}: ${w.reason}`).join('\n');
    return (
      <span
        className="type-badge"
        title={reasons}
        style={{ backgroundColor: '#F59E0B', color: '#1A1A1A', marginLeft: '6px' }}
      >Blackout</span>
    );
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'denied': return 'status-denied';
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="attendance-loading">
        <div className="loading-spinner"></div>
        <p>Loading attendance data...</p>
      </div>
    );
  }

  return (
    <div className="attendance-page">
      {/* Header */}
      <div className="attendance-header">
        <div className="header-content">
          <h1>Attendance & Time Off</h1>
          <p>Manage employee attendance, time-off requests, and workers compensation</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="attendance-stats">
        <div className="stat-card out-today" style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
          <div className="stat-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></div>
          <div className="stat-info">
            <span className="stat-value">{outToday.length}</span>
            <span className="stat-label">Out Today</span>
          </div>
        </div>
        <div className="stat-card pending" style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
          <div className="stat-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
          <div className="stat-info">
            <span className="stat-value">{pendingRequests.length}</span>
            <span className="stat-label">Pending Requests</span>
          </div>
        </div>
        <div className="stat-card workers-comp" style={{ boxShadow: '0 0 25px rgba(212,169,78,0.35), 0 0 50px rgba(212,169,78,0.15), 0 0 80px rgba(212,169,78,0.05)' }}>
          <div className="stat-icon"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg></div>
          <div className="stat-info">
            <span className="stat-value">{workersCompCases.filter(c => c.status === 'active').length}</span>
            <span className="stat-label">Active WC Cases</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="attendance-tabs">
        <button 
          className={`tab-btn ${activeTab === 'today' ? 'active' : ''}`}
          onClick={() => setActiveTab('today')}
        >
          📅 Out Today
        </button>
        <button 
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          ⏳ Pending Approval
          {pendingRequests.length > 0 && (
            <span className="tab-badge">{pendingRequests.length}</span>
          )}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          📋 All Requests
        </button>
        <button 
          className={`tab-btn ${activeTab === 'workers-comp' ? 'active' : ''}`}
          onClick={() => setActiveTab('workers-comp')}
        >
          🏥 Workers Comp
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* OUT TODAY TAB */}
        {activeTab === 'today' && (
          <div className="out-today-section">
            {outToday.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">✓</span>
                <h3>Everyone is in today!</h3>
                <p>No employees are scheduled off for today.</p>
              </div>
            ) : (
              <div className="employee-grid">
                {outToday.map((emp, index) => (
                  <div key={index} className="employee-card">
                    <div className="employee-avatar" style={{ backgroundColor: emp.color || '#A0A0A0' }}>
                      {emp.employee_name?.charAt(0) || '?'}
                    </div>
                    <div className="employee-info">
                      <h4>{emp.employee_name}</h4>
                      <p className="employee-id">ID: {emp.employee_id || 'N/A'}</p>
                      <p className="employee-dept">{emp.department || 'No Department'}</p>
                    </div>
                    <div className="leave-info">
                      <span className="leave-type" style={{ backgroundColor: emp.color || '#A0A0A0' }}>
                        {emp.leave_type}
                        {emp.workers_comp_case_id && <span className="wc-indicator">WC</span>}
                      </span>
                      <span className="leave-dates">
                        {formatDate(emp.start_date)} - {formatDate(emp.end_date)}
                      </span>
                      {emp.expected_return_date && (
                        <span className="return-date">
                          Returns: {formatDate(emp.expected_return_date)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PENDING APPROVAL TAB */}
        {activeTab === 'pending' && (
          <div className="pending-section">
            {pendingRequests.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">✓</span>
                <h3>No pending requests</h3>
                <p>All time-off requests have been reviewed.</p>
              </div>
            ) : (
              <div className="requests-table">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Type</th>
                      <th>Dates</th>
                      <th>Hours</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.map((req) => (
                      <tr key={req.id}>
                        <td>
                          <div className="employee-cell">
                            <div className="mini-avatar" style={{ backgroundColor: req.color || '#A0A0A0' }}>
                              {req.employee_name?.charAt(0)}
                            </div>
                            <div>
                              <span className="emp-name">{req.employee_name}</span>
                              <span className="emp-dept">{req.department}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="type-badge" style={{ backgroundColor: req.color }}>
                            {req.type_name}
                          </span>
                        </td>
                        <td>
                          {formatDate(req.start_date)} - {formatDate(req.end_date)}
                        </td>
                        <td>{req.hours_requested}h</td>
                        <td>
                          <span className="type-badge" style={{
                            backgroundColor: req.status === 'pending_manager' ? '#F59E0B' : '#3B82F6'
                          }}>{STAGE_LABEL[req.status] || req.status}</span>
                          <BlackoutBadge warnings={req.blackout_warnings} />
                        </td>
                        <td className="reason-cell">{req.reason || '-'}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-approve"
                              onClick={() => handleReview(req)}
                              disabled={actionLoading === req.id}
                            >
                              Review
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ALL REQUESTS TAB */}
        {activeTab === 'history' && (
          <div className="history-section">
            {/* Filters */}
            <div className="filters-bar">
              <div className="filter-group">
                <label>From</label>
                <input 
                  type="date" 
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                />
              </div>
              <div className="filter-group">
                <label>To</label>
                <input 
                  type="date" 
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                />
              </div>
              <div className="filter-group">
                <label>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="denied">Denied</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Type</label>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="all">All Types</option>
                  {timeOffTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Results */}
            <div className="requests-table">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Dates</th>
                    <th>Hours</th>
                    <th>Status</th>
                    <th>Reviewed By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allRequests.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-cell">No requests found for selected filters</td>
                    </tr>
                  ) : (
                    allRequests.map((req) => (
                      <tr key={req.id}>
                        <td>
                          <div className="employee-cell">
                            <div className="mini-avatar" style={{ backgroundColor: req.color || '#A0A0A0' }}>
                              {req.employee_name?.charAt(0)}
                            </div>
                            <div>
                              <span className="emp-name">{req.employee_name}</span>
                              <span className="emp-id">#{req.employee_id}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="type-badge" style={{ backgroundColor: req.color }}>
                            {req.type_name}
                          </span>
                        </td>
                        <td>{formatDate(req.start_date)} - {formatDate(req.end_date)}</td>
                        <td>{req.hours_requested}h</td>
                        <td>
                          <span className={`status-badge ${getStatusBadgeClass(req.status)}`}>
                            {STAGE_LABEL[req.status] || req.status}
                          </span>
                          <BlackoutBadge warnings={req.blackout_warnings} />
                        </td>
                        <td>{req.reviewed_by_name || req.manager_reviewed_by_name || req.cancelled_by_name || '-'}</td>
                        <td>
                          {req.status === 'approved' && (
                            <button
                              className="btn-secondary"
                              disabled={actionLoading === req.id}
                              onClick={() => cancelApproved(req)}
                            >Cancel</button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* WORKERS COMP TAB */}
        {activeTab === 'workers-comp' && (
          <div className="workers-comp-section">
            {workersCompCases.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">🏥</span>
                <h3>No Workers Comp Cases</h3>
                <p>There are no workers compensation cases on file.</p>
              </div>
            ) : (
              <div className="wc-cards">
                {workersCompCases.map((wc) => (
                  <div key={wc.id} className={`wc-card ${wc.status}`}>
                    <div className="wc-header">
                      <div className="wc-employee">
                        <div className="wc-avatar">
                          {wc.employee_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <h4>{wc.employee_name}</h4>
                          <span>ID: {wc.employee_id}</span>
                        </div>
                      </div>
                      <span className={`wc-status-badge ${wc.status}`}>
                        {wc.status === 'active' ? '🔴 Active' : 
                         wc.status === 'light_duty' ? '🟡 Light Duty' : 
                         wc.status === 'cleared' ? '🟢 Cleared' : '⚫ Closed'}
                      </span>
                    </div>
                    <div className="wc-details">
                      <div className="wc-detail">
                        <label>Case #</label>
                        <span>{wc.case_number || 'Pending'}</span>
                      </div>
                      <div className="wc-detail">
                        <label>Injury Date</label>
                        <span>{formatDate(wc.injury_date)}</span>
                      </div>
                      <div className="wc-detail">
                        <label>Expected Return</label>
                        <span>{formatDate(wc.expected_return_date) || 'TBD'}</span>
                      </div>
                    </div>
                    <div className="wc-description">
                      <label>Injury Description</label>
                      <p>{wc.injury_description || 'No description provided'}</p>
                    </div>
                    <div className="wc-actions">
                      <button className="btn-view" onClick={() => handleViewCase(wc.id)}>
                        View Details
                      </button>
                      {wc.status === 'active' && (
                        <button className="btn-close" onClick={() => handleCloseCase(wc.id)}>
                          Close Case
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Review Time-Off Request</h2>
              <button className="modal-close" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="review-details">
                <div className="review-row">
                  <label>Employee:</label>
                  <span>{selectedRequest.employee_name}</span>
                </div>
                <div className="review-row">
                  <label>Type:</label>
                  <span className="type-badge" style={{ backgroundColor: selectedRequest.color }}>
                    {selectedRequest.type_name}
                  </span>
                </div>
                <div className="review-row">
                  <label>Dates:</label>
                  <span>{formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)}</span>
                </div>
                <div className="review-row">
                  <label>Hours:</label>
                  <span>{selectedRequest.hours_requested} hours</span>
                </div>
                {selectedRequest.reason && (
                  <div className="review-row">
                    <label>Reason:</label>
                    <span>{selectedRequest.reason}</span>
                  </div>
                )}
              </div>
              {reviewError && (
                <div style={{
                  background: 'rgba(239,68,68,0.12)', border: '1px solid #EF4444',
                  borderRadius: '8px', padding: '10px', margin: '0 0 12px'
                }}>{reviewError}</div>
              )}
              <div className="review-notes">
                <label>Review Notes (required to deny)</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add any notes about this decision..."
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-deny"
                onClick={() => submitReview('deny')}
                disabled={actionLoading}
              >
                ✗ Deny
              </button>
              <button 
                className="btn-approve-full"
                onClick={() => submitReview('approve')}
                disabled={actionLoading}
              >
                ✓ Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workers Comp Detail Modal */}
      {showWorkersCompModal && selectedCase && (
        <div className="modal-overlay" onClick={() => setShowWorkersCompModal(false)}>
          <div className="modal-content wc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🏥 Workers Comp Case Details</h2>
              <button className="modal-close" onClick={() => setShowWorkersCompModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="wc-modal-grid">
                <div className="wc-modal-section">
                  <h3>Employee Information</h3>
                  <div className="wc-modal-row">
                    <label>Name:</label>
                    <span>{selectedCase.employee_name}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Employee ID:</label>
                    <span>{selectedCase.employee_id}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Department:</label>
                    <span>{selectedCase.department || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="wc-modal-section">
                  <h3>Case Information</h3>
                  <div className="wc-modal-row">
                    <label>Case Number:</label>
                    <span>{selectedCase.case_number || 'Pending'}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Insurance Claim:</label>
                    <span>{selectedCase.insurance_claim_number || 'N/A'}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Status:</label>
                    <span className={`wc-status-badge ${selectedCase.status}`}>
                      {selectedCase.status}
                    </span>
                  </div>
                </div>

                <div className="wc-modal-section">
                  <h3>Injury Details</h3>
                  <div className="wc-modal-row">
                    <label>Injury Date:</label>
                    <span>{formatDate(selectedCase.injury_date)}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Location:</label>
                    <span>{selectedCase.injury_location || 'N/A'}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Body Part:</label>
                    <span>{selectedCase.body_part_affected || 'N/A'}</span>
                  </div>
                  <div className="wc-modal-row full-width">
                    <label>Description:</label>
                    <p>{selectedCase.injury_description}</p>
                  </div>
                </div>

                <div className="wc-modal-section">
                  <h3>Medical Information</h3>
                  <div className="wc-modal-row">
                    <label>Physician:</label>
                    <span>{selectedCase.treating_physician || 'N/A'}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Phone:</label>
                    <span>{selectedCase.physician_phone || 'N/A'}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Facility:</label>
                    <span>{selectedCase.medical_facility || 'N/A'}</span>
                  </div>
                </div>

                <div className="wc-modal-section">
                  <h3>Return Dates</h3>
                  <div className="wc-modal-row">
                    <label>Expected Return:</label>
                    <span>{formatDate(selectedCase.expected_return_date) || 'TBD'}</span>
                  </div>
                  <div className="wc-modal-row">
                    <label>Actual Return:</label>
                    <span>{formatDate(selectedCase.actual_return_date) || 'Not yet returned'}</span>
                  </div>
                </div>

                {selectedCase.notes && (
                  <div className="wc-modal-section full-width">
                    <h3>Notes</h3>
                    <p className="wc-notes">{selectedCase.notes}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowWorkersCompModal(false)}>
                Close
              </button>
              {selectedCase.status === 'active' && (
                <button className="btn-close-case" onClick={() => handleCloseCase(selectedCase.id)}>
                  Close Case
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
